import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FontFamily } from '@zocdoc/components/src/typography';
import { FormattedMessage, injectIntl } from 'react-intl';
import { LOCALIZED_STRINGS } from './constants';
import { Textbox } from '@zocdoc/components/src/textbox';
import { Button } from '@zocdoc/components/src/button';
import { coral } from '@zocdoc/branding/colors';
import { breakpoint } from '@zocdoc/components/src/grid';

class BaseInviteDoctorForm extends PureComponent {
    static propTypes = {
        emailValid: PropTypes.bool.isRequired,
        firstNameValid: PropTypes.bool.isRequired,
        isMessageSent: PropTypes.bool.isRequired,
        lastNameValid: PropTypes.bool.isRequired,
        onBlurDoctorsEmail: PropTypes.func.isRequired,
        onBlurFirstName: PropTypes.func.isRequired,
        onBlurLastName: PropTypes.func.isRequired,
        onClickFindAnotherDoctor: PropTypes.func.isRequired,
        onClickSend: PropTypes.func.isRequired,
        professionalName: PropTypes.string.isRequired,
        submitError: PropTypes.bool.isRequired,
    };

    render() {
        const {
            intl,
            isMessageSent,
            professionalName,
            emailValid,
            firstNameValid,
            lastNameValid,
            onClickSend,
        } = this.props;
        if (!isMessageSent) {
            return (
                <Container data-test="invite-doctor-form">
                    <div data-test="invite-doctor-form-description">
                        <FormTextParagraph>
                            <WantToBookMessage>
                                <FormattedMessage
                                    {...LOCALIZED_STRINGS.WantToBookWith}
                                    values={{ professionalName }}
                                />
                            </WantToBookMessage>
                            &nbsp;
                            <FormattedMessage
                                {...LOCALIZED_STRINGS.CompleteTheForm}
                                values={{ professionalName }}
                            />
                        </FormTextParagraph>
                    </div>
                    <FormFieldContainer>
                        <FormInputHeader data-test="doctors-email">
                            <FormattedMessage {...LOCALIZED_STRINGS.DoctorsEmail} />
                        </FormInputHeader>
                        <Textbox
                            data-test="doctors-email-input"
                            invalidMessage={emailValid
                                ? undefined
                                : intl.formatMessage(LOCALIZED_STRINGS.PleaseEnterAValidEmail)}
                            onBlur={this.onBlurDoctorsEmail}
                            placeholder={intl.formatMessage(LOCALIZED_STRINGS.EmailAddressPlaceholder)}
                        />
                    </FormFieldContainer>
                    <FormFieldContainer>
                        <FormInputHeader data-test="first-name">
                            <FormattedMessage {...LOCALIZED_STRINGS.YourFirstName} />
                        </FormInputHeader>
                        <Textbox
                            data-test="first-name-input"
                            invalidMessage={firstNameValid
                                ? undefined
                                : intl.formatMessage(LOCALIZED_STRINGS.PleaseEnterAFirstName)}
                            onBlur={this.onBlurFirstName}
                            placeholder={intl.formatMessage(LOCALIZED_STRINGS.FirstNamePlaceholder)}
                        />
                    </FormFieldContainer>
                    <FormFieldContainer>
                        <FormInputHeader data-test="last-name">
                            <FormattedMessage {...LOCALIZED_STRINGS.YourLastName} />
                        </FormInputHeader>
                        <Textbox
                            data-test="last-name-input"
                            invalidMessage={lastNameValid
                                ? undefined
                                : intl.formatMessage(LOCALIZED_STRINGS.PleaseEnterALastName)}
                            onBlur={this.onBlurLastName}
                            placeholder={intl.formatMessage(LOCALIZED_STRINGS.LastNamePlaceholder)}
                        />
                    </FormFieldContainer>
                    <FullWidthButton
                        data-test="send-button"
                        onClick={onClickSend}
                        style={Button.STYLE.TRANSPARENT}
                    >
                        <FormattedMessage {...LOCALIZED_STRINGS.Send} />
                    </FullWidthButton>
                    {this.maybeRenderSubmitError()}
                </Container>
            );
        } else {
            return (
                <Container data-test="message-sent-form">
                    <FormTextParagraph data-test="message-sent">
                        <FormattedMessage {...LOCALIZED_STRINGS.MessageSent} />
                    </FormTextParagraph>
                    <FormTextParagraphNoMargin data-test="message-sent-description">
                        <FormattedMessage
                            {...LOCALIZED_STRINGS.MessageSentDescription}
                            values={{ professionalName }}
                        />
                    </FormTextParagraphNoMargin>
                    <FullWidthButton
                        data-test="find-another-doctor-button"
                        onClick={this.handleClickFindAnotherDoctor}
                        style={Button.STYLE.TRANSPARENT}
                    >
                        <FormattedMessage {...LOCALIZED_STRINGS.FindAnotherDoctor} />
                    </FullWidthButton>
                </Container>
            );
        }
    }

    maybeRenderSubmitError = () => {
        if (this.props.submitError) {
            return (
                <FormInputError data-test="submit-error">
                    <FormattedMessage {...LOCALIZED_STRINGS.SomethingWentWrong} />
                </FormInputError>
            );
        }
    };

    onBlurDoctorsEmail = e => this.props.onBlurDoctorsEmail(e.currentTarget.value);
    onBlurFirstName = e => { this.props.onBlurFirstName(e.currentTarget.value); };
    onBlurLastName = e => { this.props.onBlurLastName(e.currentTarget.value); };

    handleClickFindAnotherDoctor = async () => {
        this.props.onClickFindAnotherDoctor();
        window.location.assign('/search');
    };
}

const Container = styled.div`
    padding: 30px;

    ${breakpoint.small} {
        padding: 30px 60px;
    }

    ${breakpoint.medium} {
        padding: 12px 0 16px 0;
    }
`;

const FormFieldContainer = styled.div`
    margin-top: 16px;
`;

const FormTextParagraph = styled.p`
    font: 15px/24px ${FontFamily.SharpSansSemibold};
    margin-bottom: 24px;
    ${breakpoint.medium} {
        font: 18px/28px ${FontFamily.SharpSansSemibold};
    }
`;

const FormTextParagraphNoMargin = styled(FormTextParagraph)`
    margin-bottom: 0;
`;

const WantToBookMessage = styled.span`
    font-family: ${FontFamily.SharpSansBold};
`;

const FormInputHeader = styled.h3`
    font: 15px/24px ${FontFamily.SharpSansBold};
    margin-bottom: 4px;
    ${breakpoint.medium} {
        font: 18px/28px ${FontFamily.SharpSansBold};
    }
`;

const FormInputError = styled.p`
    margin-top: 4px;
    font: 15/24px ${FontFamily.SharpSansMedium};
    color: ${coral};
    ${breakpoint.medium} {
        font: 18/28px ${FontFamily.SharpSansMedium};
    }
`;

const FullWidthButton = styled(Button)`
    width: 100%;
    margin-top: 30px;
`;

export const InviteDoctorForm = injectIntl(BaseInviteDoctorForm);
